import * as React from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { getTranslate } from 'react-localize-redux';
import { bindActionCreators } from 'redux';

import {
    withStyles, Typography, Button, TextField, FormControl, FormControlLabel, Checkbox,
} from '@material-ui/core';

import * as PatientStore from '../../../app/store/PatientStore';
import {
    validateAllFields, validateEmail, validateField, validateRequired,
} from '../../../youwell-common/validationUtils';
import { hasErrors } from './utils';
import { orgLicence } from '../../../youwell-common/constants';
import { DialogButton } from '../../../youwell-common/components';

const styles = theme => ({
    root: {
    },
    fields: {
        marginTop: theme.spacing(4),
    },
    inputField: {
        width: '50%',
        marginBottom: theme.spacing(4),
        paddingRight: theme.spacing(4),
    },
    userAccessField: {

    },
    disclaimer: {
    },
    actions: {
        marginTop: theme.spacing(4),
        padding: theme.spacing(1),
        display: 'flex',
        justifyContent: 'flex-end',
    },
    button: {
        marginRight: theme.spacing(1),
    },
    flex: {
        flex: '1 1 auto',
    },
    deleteButton: {
        justifySelf: 'flex-start',
        color: theme.palette.error.main,
    },
});

const getValidators = canLogin => ({
    name: validateRequired,
    role: validateRequired,
    email: canLogin ? [validateEmail, validateRequired] : validateEmail,
    phone: canLogin ? validateRequired : null,
});

const defaultData = {
    name: '',
    role: '',
    phone: '',
    email: '',
    canLogin: false,
};

class EditContactPerson extends React.Component {
    state = {
        ...this.props.contactData || defaultData,
        hasUnsavedChanges: false,
        validationErrors: {},
    };

    handleChange = name => (event) => {
        this.setState({
            [name]: event.target.value,
            hasUnsavedChanges: true,
        }, () => {
            const validators = getValidators(this.state.canLogin);
            const validationErrors = validateField(this.state, validators[name], name); // eslint-disable-line react/no-access-state-in-setstate
            this.setState({ validationErrors });
        });
    };

    toggleCheckedChange = name => (event) => {
        this.setState({
            [name]: event.target.checked,
            hasUnsavedChanges: true,
        });
    }

    save = () => {
        const validators = getValidators(this.state.canLogin);
        const validationErrors = validateAllFields(this.state, validators);

        if (!hasErrors(validationErrors)) {
            const saveData = Object.keys(this.props.contactData || defaultData).reduce((prev, curr) => (
                {
                    ...prev,
                    [curr]: this.state[curr],
                }), { patientId: this.props.patientId });

            if (this.props.contactData?.id) {
                this.props.updateContact(saveData, { reducerParams: { patientId: this.props.patientId }, callback: this.props.onClose });
            } else {
                this.props.insertContact(saveData, { reducerParams: { patientId: this.props.patientId }, callback: this.props.onClose });
            }
        } else {
            this.setState({ validationErrors });
        }
    }

    delete = () => {
        this.props.deleteContact(this.props.contactData, { reducerParams: { patientId: this.props.patientId, contactId: this.props.contactData.id }, callback: this.props.onClose });
    }

    getError = (name) => {
        const error = this.state.validationErrors[name];
        return error ? this.props.translate(error) : null;
    };

    cancel = () => {
        this.props.onClose();
    }

    render() {
        const {
            classes, translate, contactData, profileData,
        } = this.props;

        const hasOrgLicense = profileData?.selectedRole.license >= orgLicence.Trial;

        return (
            <div className={classes.root}>
                <Typography variant="caption">{translate('studentEdit.contactPersonDescription')}</Typography>

                <div className={classes.fields}>
                    <TextField
                        className={classes.inputField}
                        label={translate('studentEdit.name')}
                        value={this.state.name || ''}
                        onChange={this.handleChange('name')}
                        error={!!this.getError('name')}
                        helperText={this.getError('name')}
                        InputLabelProps={{ shrink: true }}
                        required
                    />
                    <TextField
                        className={classes.inputField}
                        label={translate('studentEdit.role')}
                        value={this.state.role || ''}
                        onChange={this.handleChange('role')}
                        error={!!this.getError('role')}
                        helperText={this.getError('role')}
                        InputLabelProps={{ shrink: true }}
                        required
                    />
                    <TextField
                        className={classes.inputField}
                        label={translate('studentEdit.phone')}
                        value={this.state.phone || ''}
                        onChange={this.handleChange('phone')}
                        error={!!this.getError('phone')}
                        helperText={this.getError('phone')}
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        className={classes.inputField}
                        label={translate('studentEdit.email')}
                        value={this.state.email || ''}
                        onChange={this.handleChange('email')}
                        error={!!this.getError('email')}
                        helperText={this.getError('email')}
                        InputLabelProps={{ shrink: true }}
                        type="email"
                    />
                </div>

                {hasOrgLicense && (
                    <FormControl className={classes.userAccessField}>
                        <FormControlLabel
                            control={(
                                <Checkbox
                                    checked={!!this.state.canLogin}
                                    onChange={this.toggleCheckedChange('canLogin')}
                                    value="userAccess"
                                    color="primary"
                                />
                            )}
                            label={translate('studentEdit.hasUserAccess')}
                        />
                        {contactData?.canLogin && !this.state.canLogin ? (
                            <Typography color="error" className={classes.disclaimer}>
                                {translate('studentEdit.userRemoveAccessDisclaimer')}
                            </Typography>
                        ) : (
                            <Typography color={this.state.canLogin ? 'primary' : 'textSecondary'} className={classes.disclaimer}>
                                {translate('studentEdit.userAccessDisclaimer')}
                            </Typography>
                        )}
                    </FormControl>
                )}


                <div className={classes.actions}>
                    {contactData && (
                        <DialogButton
                            dialogTitle={translate('studentEdit.deleteContactConfirm')}
                            buttonText={translate('studentEdit.deleteContact')}
                            dialogCancelText={translate('common.cancel')}
                            dialogOkAction={this.delete}
                            buttonProps={{
                                className: classes.deleteButton,
                            }}
                        />
                    )}
                    <div className={classes.flex} />
                    <Button
                        className={classes.button}
                        color="secondary"
                        onClick={this.cancel}
                    >
                        {translate('common.cancel')}
                    </Button>
                    <Button
                        className={classes.button}
                        color="primary"
                        variant="contained"
                        onClick={this.save}
                        disabled={!this.state.hasUnsavedChanges}
                    >
                        {translate('common.save')}
                    </Button>

                </div>
            </div>
        );
    }
}

EditContactPerson.propTypes = {
    classes: PropTypes.object.isRequired,
    translate: PropTypes.func.isRequired,
    patientId: PropTypes.string.isRequired,
    contactData: PropTypes.object,
    updateContact: PropTypes.func.isRequired,
    insertContact: PropTypes.func.isRequired,
    deleteContact: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    profileData: PropTypes.object.isRequired,
};

EditContactPerson.defaultProps = {
    contactData: null,
};

const mapStateToProps = state => ({
    translate: getTranslate(state.localize),
    profileData: state.auth.profileData,
    clientConfig: state.auth.clientConfig,
});

const mapDispatchToProps = dispatch => ({
    ...bindActionCreators(PatientStore.actionCreators, dispatch),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(EditContactPerson)));
